/**
 * Выбор сделки CRM для привязки задачи (новый бэк).
 *
 * Один список на два места: панель привязки в форме создания и шапка карточки
 * задачи. Источник — зеркало `/crm/deals/list` с поиском по названию; сама
 * привязка живёт в `/v2/tasks/{id}/crm-links` и делается вызывающим.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  searchDealsV2,
  type CrmDealV2,
} from '@/entities/task/api/crm-links-v2';
import { useDebounced } from '@/hooks/ui/useDebounced';

interface Props {
  /** Уже выбранная сделка — подсвечивается в списке. */
  selectedId?: string | null;
  onPick: (deal: CrmDealV2) => void;
  /** Показывать строку «Без привязки» (снять привязку). */
  onClear?: () => void;
}

export function CrmDealPickerV2({ selectedId, onPick, onClear }: Props) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 350);

  const dealsQ = useQuery({
    queryKey: ['crm-deals-search-v2', debounced.trim()],
    queryFn: () => searchDealsV2(debounced.trim()),
    staleTime: 60_000,
  });

  const deals = dealsQ.data ?? [];

  return (
    <div className="flex flex-col gap-[8px]">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск сделки по названию"
        autoFocus
        className="w-full rounded-[10px] bg-[#F1F3F8] px-[12px] py-[9px] text-[12px] text-[#575757] outline-none placeholder:text-[rgba(20,30,49,0.42)]"
      />

      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="rounded-[10px] px-[10px] py-[8px] text-left text-[12px] text-[rgba(20,30,49,0.6)] hover:bg-[rgba(59,77,127,0.06)]"
        >
          Без привязки
        </button>
      ) : null}

      {dealsQ.isPending ? (
        <div className="px-[10px] py-[10px] text-[12px] text-[rgba(20,30,49,0.5)]">
          Загрузка…
        </div>
      ) : null}

      {!dealsQ.isPending && deals.length === 0 ? (
        <div className="px-[10px] py-[10px] text-[12px] text-[rgba(20,30,49,0.5)]">
          Сделок не нашлось
        </div>
      ) : null}

      <div className="flex max-h-[300px] flex-col overflow-y-auto">
        {deals.map((deal) => {
          const id = String(deal.id ?? '');
          const active = selectedId === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onPick(deal)}
              className="flex items-center gap-[10px] rounded-[10px] px-[10px] py-[9px] text-left hover:bg-[rgba(59,77,127,0.06)]"
              style={
                active ? { background: 'rgba(87,183,146,0.12)' } : undefined
              }
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] font-semibold text-[#141E31]">
                  {deal.title || `Сделка #${id}`}
                </span>
                <span className="block truncate text-[11px] text-[rgba(20,30,49,0.5)]">
                  {[deal.assignedByName, deal.stage?.name]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="shrink-0 text-[11px] text-[rgba(20,30,49,0.45)]">
                #{id}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
